import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Paper,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  Divider,
  Avatar,
} from "@mui/material";
import { format } from "date-fns";
import subscriptionHistoryService from "../../services/subscriptionHistoryService";
import { useAuth } from "../../context/AuthContext";

const SubscriptionHistory = () => {
  const { user } = useAuth();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (user?.organizationId) {
      loadHistory();
    }
  }, [user?.organizationId]);

  const loadHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await subscriptionHistoryService.getSubscriptionHistory(
        user.organizationId
      );
      setHistory(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error loading subscription history:", err);
      setError("Failed to load subscription history");
    } finally {
      setLoading(false);
    }
  };

  const getActionTypeColor = (actionType) => {
    switch (actionType) {
      case "ONBOARD":
        return "success";
      case "RENEW":
        return "info";
      case "UPGRADE":
        return "warning";
      case "DOWNGRADE":
        return "error";
      case "CANCEL":
        return "default";
      default:
        return "default";
    }
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(amount);
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    try {
      // Parse the date string - handle both ISO format and database timestamp format
      // Backend format: "2025-12-28 22:09:05.467072" (assumed to be in server timezone/IST)
      let date;
      if (typeof dateString === 'string') {
        // If it's in ISO format with 'T', parse directly
        if (dateString.includes('T')) {
          date = new Date(dateString);
        } else if (dateString.includes(' ')) {
          // Format: "2025-12-28 22:09:05.467072" 
          // Parse as local time by creating date components manually
          // This prevents timezone conversion issues
          const dateTimeParts = dateString.split(' ');
          const datePart = dateTimeParts[0]; // "2025-12-28"
          const timePart = dateTimeParts[1]?.split('.')[0] || ''; // "22:09:05"
          
          if (datePart && timePart) {
            // Create date in local timezone
            const [year, month, day] = datePart.split('-').map(Number);
            const [hours, minutes, seconds] = timePart.split(':').map(Number);
            date = new Date(year, month - 1, day, hours, minutes, seconds || 0);
          } else {
            date = new Date(dateString);
          }
        } else {
          date = new Date(dateString);
        }
      } else {
        date = new Date(dateString);
      }
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return "Invalid Date";
      }
      
      // Format date part
      const datePart = format(date, "MM/dd/yyyy");
      
      // Format time in 12-hour format with AM/PM
      let hours = date.getHours();
      const minutes = date.getMinutes();
      const seconds = date.getSeconds();
      const ampm = hours >= 12 ? 'PM' : 'AM';
      
      // Convert to 12-hour format
      hours = hours % 12;
      hours = hours ? hours : 12; // 0 should be 12
      
      // Format with leading zeros
      const hoursStr = hours.toString().padStart(2, '0');
      const minutesStr = minutes.toString().padStart(2, '0');
      const secondsStr = seconds.toString().padStart(2, '0');
      
      return `${datePart}, ${hoursStr}:${minutesStr}:${secondsStr} ${ampm}`;
    } catch (error) {
      console.error("Error formatting date:", error, dateString);
      return "Invalid Date";
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  if (history.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Subscription History
        </Typography>
        <Alert severity="info">No subscription history found.</Alert>
      </Box>
    );
  }

  const getActionTypeInitial = (actionType) => {
    switch (actionType) {
      case "ONBOARD":
        return "O";
      case "RENEW":
        return "R";
      case "UPGRADE":
        return "↑";
      case "DOWNGRADE":
        return "↓";
      case "CANCEL":
        return "X";
      default:
        return "?";
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Subscription History
      </Typography>

      <List>
        {history.map((item, index) => {
          // Handle subscription type - can be object or string
          const subscriptionTypeName = item.subscriptionType?.subType || 
                                       item.subscriptionType || 
                                       "Unknown";
          
          const actionColor = getActionTypeColor(item.actionType);
          const avatarBgColor = 
            actionColor === "success" ? "#4caf50" :
            actionColor === "info" ? "#2196f3" :
            actionColor === "warning" ? "#ff9800" :
            actionColor === "error" ? "#f44336" :
            "#9e9e9e";
          
          return (
            <React.Fragment key={item.id}>
              <ListItem alignItems="flex-start" sx={{ mb: 2 }}>
                <Avatar
                  sx={{
                    bgcolor: avatarBgColor,
                    mr: 2,
                    width: 40,
                    height: 40,
                  }}
                >
                  {getActionTypeInitial(item.actionType)}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Paper elevation={2} sx={{ p: 2 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="h6">
                        {item.actionType} - {subscriptionTypeName}
                      </Typography>
                      <Chip
                        label={item.status}
                        size="small"
                        color={item.status === "ACTIVE" ? "success" : "default"}
                      />
                    </Box>

                    <Typography variant="body2" color="text.secondary">
                      Package: {item.packageType}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Amount: {formatAmount(item.amount)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Period: {new Date(item.startDate).toLocaleDateString()} -{" "}
                      {new Date(item.endDate).toLocaleDateString()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Payment: {item.paymentMethod} ({item.paymentStatus})
                    </Typography>

                    {item.notes && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Notes: {item.notes}
                      </Typography>
                    )}

                    <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                      {formatDateTime(item.createdAt)}
                    </Typography>
                  </Paper>
                </Box>
              </ListItem>
              {index < history.length - 1 && <Divider variant="inset" component="li" sx={{ ml: 6 }} />}
            </React.Fragment>
          );
        })}
      </List>
    </Box>
  );
};

export default SubscriptionHistory;

